import type { DemoDataset } from "../types";

/**
 * 1. Dr. Sarah Chen — Professor of Computer Science
 * Tenured faculty persona: algorithm design, distributed computing, and machine learning systems.
 */
export const drChenDataset: DemoDataset = {
  id: "persona-dr-chen",
  personaName: "Dr. Sarah Chen",
  academicTitle: "Professor of Computer Science & Engineering",
  primaryDiscipline: "Computer Science",
  profile: {
    fullName: "Dr. Sarah Chen",
    title: "Professor of Computer Science & Engineering",
    bio: "Principal Investigator of the Distributed Intelligence Lab. My research focuses on distributed consensus protocols, resilient fault tolerance in edge networks, and scalable machine learning infrastructure. Teaching distributed systems and algorithm analysis.",
    photoUrl: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=600",
  },
  sections: [
    {
      id: "chen-sec-about",
      type: "about",
      visible: true,
      displayOrder: 0,
      content: {
        text: "I am a Professor in the Department of Computer Science and Engineering. Prior to joining academia, I completed my Ph.D. at MIT and worked as a visiting research scientist at Bell Labs. My group explores the intersection of distributed consensus, asynchronous networks, and verifiable computing.",
      },
    },
    {
      id: "chen-sec-pubs",
      type: "publications",
      visible: true,
      displayOrder: 1,
      content: {
        items: [
          {
            id: "pub-1",
            title: "Byzantine Fault Tolerance at Scale: Asynchronous Consensus for Edge Clouds",
            authors: "S. Chen, M. Vance, K. Patel",
            venue: "ACM Symposium on Operating Systems Principles (SOSP)",
            year: "2025",
            doi: "10.1145/3477132.3483561",
            highlighted: true,
          },
          {
            id: "pub-2",
            title: "Adaptive Rate Control in Decentralized Gradient Aggregation",
            authors: "S. Chen, R. Liu",
            venue: "IEEE Transactions on Parallel and Distributed Systems (TPDS)",
            year: "2024",
            doi: "10.1109/TPDS.2024.102381",
          },
          {
            id: "pub-3",
            title: "Deterministic Execution for Stateful Serverless Functions",
            authors: "T. Gomez, S. Chen, H. Tanaka",
            venue: "USENIX Annual Technical Conference (USENIX ATC)",
            year: "2023",
          },
        ],
      },
    },
    {
      id: "chen-sec-teaching",
      type: "teaching",
      visible: true,
      displayOrder: 2,
      content: {
        items: [
          {
            id: "teach-1",
            courseNumber: "CS 6450",
            title: "Advanced Distributed Systems",
            role: "Instructor",
            term: "Fall 2025",
          },
          {
            id: "teach-2",
            courseNumber: "CS 3510",
            title: "Design and Analysis of Algorithms",
            role: "Instructor",
            term: "Spring 2025",
          },
        ],
      },
    },
    {
      id: "chen-sec-experience",
      type: "experience",
      visible: true,
      displayOrder: 3,
      content: {
        items: [
          {
            id: "exp-1",
            institution: "University Institute of Technology",
            role: "Professor of Computer Science",
            period: "2020 – Present",
          },
          {
            id: "exp-2",
            institution: "University Institute of Technology",
            role: "Associate Professor",
            period: "2015 – 2020",
          },
        ],
      },
    },
    {
      id: "chen-sec-contact",
      type: "contact",
      visible: true,
      displayOrder: 4,
      content: {
        email: "sarah.chen@university.edu",
        office: "Gates Hall, Room 412",
        links: [
          { label: "Google Scholar", url: "https://scholar.google.com" },
          { label: "GitHub", url: "https://github.com" },
        ],
      },
    },
  ],
};

/**
 * 2. Dr. James Morrison — Department Head & Professor of Physics
 * Leadership persona: quantum condensed matter, lab equipment, and federal grant stewardship.
 */
export const drMorrisonDataset: DemoDataset = {
  id: "persona-dr-morrison",
  personaName: "Dr. James Morrison",
  academicTitle: "Department Chair & Professor of Condensed Matter Physics",
  primaryDiscipline: "Physics",
  profile: {
    fullName: "Dr. James Morrison",
    title: "Department Chair & Professor of Condensed Matter Physics",
    bio: "Chair of Physics and Director of the Quantum Matter Laboratory. Leading experimental investigations into topological insulators, superconducting quantum circuits, and low-temperature scanning tunneling microscopy.",
    photoUrl: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=600",
  },
  sections: [
    {
      id: "morrison-sec-about",
      type: "about",
      visible: true,
      displayOrder: 0,
      content: {
        text: "My group uses cryogenic scanning tunneling microscopy and angle-resolved photoemission spectroscopy to probe electronic phenomena at the nanoscale. In addition to our laboratory research, I have served as Physics Department Chair since 2021.",
      },
    },
    {
      id: "morrison-sec-projects",
      type: "projects",
      visible: true,
      displayOrder: 1,
      content: {
        items: [
          {
            id: "proj-1",
            title: "NSF Quantum Foundry Initiative: Topological Heterostructures",
            description: "Directing a multi-institution program to fabricate clean interfaces for topological qubits.",
            grantNumber: "NSF DMR-2219481",
            amount: "$4.2M",
            period: "2023 – 2028",
          },
          {
            id: "proj-2",
            title: "Millikelvin STM Imaging of Fractional Quantum Hall States",
            description: "Ultra-low temperature local spectroscopic mapping of 2D electron gases in magnetic fields up to 14T.",
            period: "2022 – 2026",
          },
        ],
      },
    },
    {
      id: "morrison-sec-pubs",
      type: "publications",
      visible: true,
      displayOrder: 2,
      content: {
        items: [
          {
            id: "pub-m1",
            title: "Direct Observation of Chiral Majorana Modes in Topological Superconductors",
            authors: "J. Morrison, A. Kournikova, E. Davies",
            venue: "Physical Review Letters",
            year: "2025",
            doi: "10.1103/PhysRevLett.134.026801",
            highlighted: true,
          },
          {
            id: "pub-m2",
            title: "Scanning Tunneling Spectroscopy of Moiré Flat Bands in Twisted Bilayer Graphene",
            authors: "J. Morrison, C. Zhang",
            venue: "Nature Physics",
            year: "2024",
          },
        ],
      },
    },
    {
      id: "morrison-sec-contact",
      type: "contact",
      visible: true,
      displayOrder: 3,
      content: {
        email: "j.morrison@physics.univ.edu",
        office: "Barton Hall, Physics Suite 101",
      },
    },
  ],
};

/**
 * 3. Dr. Elena Vasquez — Associate Professor & Science Communicator
 * Public engagement, evolutionary biology, monographs, and media appearances.
 */
export const drVasquezDataset: DemoDataset = {
  id: "persona-dr-vasquez",
  personaName: "Dr. Elena Vasquez",
  academicTitle: "Associate Professor of Evolutionary Ecology & Public Scholar",
  primaryDiscipline: "Ecology & Evolutionary Biology",
  profile: {
    fullName: "Dr. Elena Vasquez",
    title: "Associate Professor of Evolutionary Ecology & Public Scholar",
    bio: "Field biologist studying ecological genomics in marine archipelagos. Author of 'The Coral Archive' (Princeton Univ. Press) and regular contributor to Scientific American and NPR Science Friday.",
    photoUrl: "https://images.unsplash.com/photo-1580894732444-8ecded7900cd?auto=format&fit=crop&q=80&w=600",
  },
  sections: [
    {
      id: "vasquez-sec-about",
      type: "about",
      visible: true,
      displayOrder: 0,
      content: {
        text: "I combine field expeditions, historical museum specimens, and next-generation sequencing to understand how marine communities adapt to rapid ocean acidification.",
      },
    },
    {
      id: "vasquez-sec-pubs",
      type: "publications",
      visible: true,
      displayOrder: 1,
      content: {
        items: [
          {
            id: "pub-v1",
            title: "The Coral Archive: Centuries of Ocean Change Told Through Reef Calcification",
            authors: "E. Vasquez",
            venue: "Princeton University Press",
            year: "2024",
            highlighted: true,
          },
          {
            id: "pub-v2",
            title: "Genomic Signatures of Thermal Resilience in Indo-Pacific Coral Holobionts",
            authors: "E. Vasquez, D. Thorne, M. Al-Fassi",
            venue: "Science Advances",
            year: "2024",
          },
        ],
      },
    },
    {
      id: "vasquez-sec-contact",
      type: "contact",
      visible: true,
      displayOrder: 2,
      content: {
        email: "elena.vasquez@univ.edu",
        office: "Darwin Hall 308",
      },
    },
  ],
};

/**
 * 4. Dr. Michael Brooks — Senior Research Fellow (Computational Biology)
 * Data commons, bioinformatics pipelines, open datasets, and high-throughput algorithms.
 */
export const drBrooksDataset: DemoDataset = {
  id: "persona-dr-brooks",
  personaName: "Dr. Michael Brooks",
  academicTitle: "Senior Research Fellow in Computational Genomics",
  primaryDiscipline: "Computational Biology",
  profile: {
    fullName: "Dr. Michael Brooks",
    title: "Senior Research Fellow in Computational Genomics",
    bio: "Developing open-source statistical pipelines and deep learning models for single-cell spatial transcriptomics. Lead maintainer of the scSpatial Bioconductor library.",
    photoUrl: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=600",
  },
  sections: [
    {
      id: "brooks-sec-about",
      type: "about",
      visible: true,
      displayOrder: 0,
      content: {
        text: "My group focuses on high-dimensional biological data integration, benchmarking spatial deconvolution algorithms, and constructing reproducible bioinformatics workflows in Nextflow.",
      },
    },
    {
      id: "brooks-sec-projects",
      type: "projects",
      visible: true,
      displayOrder: 1,
      content: {
        items: [
          {
            id: "proj-b1",
            title: "scSpatial: Open Source Spatial Transcriptomics Toolkit",
            description: "High-performance R/Rust library for cell-type mapping in heterogeneous tissue slices.",
            linkUrl: "https://github.com",
          },
        ],
      },
    },
    {
      id: "brooks-sec-pubs",
      type: "publications",
      visible: true,
      displayOrder: 2,
      content: {
        items: [
          {
            id: "pub-b1",
            title: "Benchmarking Single-Cell Spatial Deconvolution Across 24 Tissue Atlases",
            authors: "M. Brooks, K. Nygard, L. Zhao",
            venue: "Nature Biotechnology",
            year: "2025",
            highlighted: true,
          },
        ],
      },
    },
    {
      id: "brooks-sec-contact",
      type: "contact",
      visible: true,
      displayOrder: 3,
      content: {
        email: "m.brooks@genomics-center.org",
      },
    },
  ],
};

/**
 * 5. Dr. Priya Sharma — Postdoctoral Fellow
 * Postdoc persona: robotics, job market candidate, conference presentations, and technical patents.
 */
export const drSharmaDataset: DemoDataset = {
  id: "persona-dr-sharma",
  personaName: "Dr. Priya Sharma",
  academicTitle: "Postdoctoral Research Fellow in Autonomous Systems",
  primaryDiscipline: "Robotics & AI",
  profile: {
    fullName: "Dr. Priya Sharma",
    title: "Postdoctoral Research Fellow in Autonomous Systems",
    bio: "Postdoc in the Autonomous Navigation Lab. Focusing on real-time visual-inertial odometry, aerial swarm coordination, and safe reinforcement learning in GPS-denied environments. On the academic job market.",
    photoUrl: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=600",
  },
  sections: [
    {
      id: "sharma-sec-about",
      type: "about",
      visible: true,
      displayOrder: 0,
      content: {
        text: "I received my Ph.D. in Mechanical Engineering from Stanford University in 2023. My research aims to enable small autonomous drones to navigate cluttered subterranean caverns with zero human intervention.",
      },
    },
    {
      id: "sharma-sec-pubs",
      type: "publications",
      visible: true,
      displayOrder: 1,
      content: {
        items: [
          {
            id: "pub-s1",
            title: "Subterranean Swarm SLAM: Robust Decentralized State Estimation for Aerial Robots",
            authors: "P. Sharma, J. Becker, S. Levine",
            venue: "IEEE International Conference on Robotics and Automation (ICRA)",
            year: "2025",
            highlighted: true,
          },
        ],
      },
    },
    {
      id: "sharma-sec-contact",
      type: "contact",
      visible: true,
      displayOrder: 2,
      content: {
        email: "psharma@robotics.univ.edu",
      },
    },
  ],
};

/**
 * 6. Alex Rivera — PhD Candidate (HCI & Accessible Tech)
 * PhD student persona: user research, conference talks, defense countdown, and code repositories.
 */
export const alexRiveraDataset: DemoDataset = {
  id: "persona-alex-rivera",
  personaName: "Alex Rivera",
  academicTitle: "Ph.D. Candidate in Human-Computer Interaction",
  primaryDiscipline: "Human-Computer Interaction",
  profile: {
    fullName: "Alex Rivera",
    title: "Ph.D. Candidate in Human-Computer Interaction",
    bio: "Advised by Prof. Sarah Chen. Designing haptic feedback wearables and non-visual spatial audio interfaces for blind and low-vision programmers. Defending Summer 2026.",
    photoUrl: "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&q=80&w=600",
  },
  sections: [
    {
      id: "rivera-sec-about",
      type: "about",
      visible: true,
      displayOrder: 0,
      content: {
        text: "My dissertation investigates tactile IDE interfaces that represent Abstract Syntax Trees using dynamic pin arrays, enabling developers with visual impairments to navigate complex codebases rapidly.",
      },
    },
    {
      id: "rivera-sec-pubs",
      type: "publications",
      visible: true,
      displayOrder: 1,
      content: {
        items: [
          {
            id: "pub-r1",
            title: "AudioTrees: Navigating Complex ASTs via Binaural Spatialization",
            authors: "A. Rivera, S. Chen",
            venue: "ACM Conference on Human Factors in Computing Systems (CHI)",
            year: "2025",
            highlighted: true,
          },
        ],
      },
    },
    {
      id: "rivera-sec-contact",
      type: "contact",
      visible: true,
      displayOrder: 2,
      content: {
        email: "alex.rivera@cs.univ.edu",
      },
    },
  ],
};

export const ALL_DEMO_DATASETS: DemoDataset[] = [
  drChenDataset,
  drMorrisonDataset,
  drVasquezDataset,
  drBrooksDataset,
  drSharmaDataset,
  alexRiveraDataset,
];
